/**
 * Channel framework contract (ENGINE-SPEC §3.1, §3.8). One ChannelImpl per seed channel; none merged (ED-3).
 * Rate/proportion channels implement `measure()` only and get everything else from `rateChannel()`
 * (framework.ts). Recurrence, distributional and structural channels implement `run()`.
 */
import type { Doc } from '../document/types.ts';
import type { JointSegmentation } from '../segments/types.ts';

export const CHANNEL_IDS = [
	'LX-01',
	'LX-02',
	'LX-03',
	'PT-01',
	'PT-02',
	'RH-01',
	'RH-02',
	'RH-03',
	'ST-01',
	'ST-02',
	'ST-03',
	'ST-04',
	'SG-01',
	'NA-01',
	'FM-01',
	'SF-04',
	'DS-01',
	'DS-02',
	'DS-03',
	'SF-05',
	'MT-01',
	'OB-01',
	'SF-01',
	'SF-02',
	'SF-03',
	'TX-01',
	'SR-01',
	'PROC-01',
	'PROC-02',
	'PROC-03',
	'PROC-04',
	'PROC-05',
	'PROC-06',
	'PROC-07',
	'PROC-08',
	// T2 (DESIGN CD-44): the two translation-axis lexical-preference channels. Appended after the original 35 so no
	// existing index moves; the original 35 are frozen in LEGACY_CHANNEL_IDS.
	'TR-01',
	'TR-02',
	// REFIT-S (DESIGN CD-119, ED-168): the short-text lexical-variation channel. Appended after TR-02, outside
	// LEGACY_CHANNEL_IDS and outside the seed-rules composite (SHORT_TEXT_CHANNEL_IDS).
	'LX-04',
] as const;
export type ChannelId = (typeof CHANNEL_IDS)[number];

/**
 * The 35 channels that existed before T2, in their original order. Every cross-channel aggregate that is part of the
 * authorship pipeline or of the stored C2 feature vector (global totals, joint segmentation, the FDR family) runs over
 * this list, so adding a channel to `CHANNEL_IDS` cannot move a byte of an existing output (T2 gate G1). Frozen: never
 * edit, never append.
 */
export const LEGACY_CHANNEL_IDS = Object.freeze(
	CHANNEL_IDS.slice(0, 35)
) as readonly LegacyChannelId[];
/** The 35 pre-T2 channel ids as a type. */
export type LegacyChannelId = Exclude<ChannelId, 'TR-01' | 'TR-02' | 'LX-04'>;

/** The T2 translation-axis lexical-preference channels (outside the legacy aggregates). */
export const TR_CHANNEL_IDS = Object.freeze(['TR-01', 'TR-02'] as const) as readonly ChannelId[];

/**
 * Channels that are evidence and model inputs but take no part in the seed-rules composite, the document bootstrap's
 * samplers or the segment index (REFIT-S, ED-168). LX-04 is scored only below 1,000 prose words and has no sampler: the
 * text-sampling interval holds it at its point value. Adding a channel here cannot move a byte of the existing composite.
 */
export const SHORT_TEXT_CHANNEL_IDS = Object.freeze(['LX-04'] as const) as readonly ChannelId[];
const SHORT_TEXT_SET: ReadonlySet<ChannelId> = new Set(SHORT_TEXT_CHANNEL_IDS);
/** Whether `id` is outside the seed composite and the samplers (see `SHORT_TEXT_CHANNEL_IDS`). */
export const isShortTextChannel = (id: ChannelId): boolean => SHORT_TEXT_SET.has(id);

const LEGACY_SET: ReadonlySet<ChannelId> = new Set(LEGACY_CHANNEL_IDS);
/** Whether `id` is one of the original 35 channels. */
export const isLegacyChannel = (id: ChannelId): boolean => LEGACY_SET.has(id);

export type Family =
	| 'VOCAB'
	| 'FORMULA'
	| 'RHETORIC'
	| 'FIGURE'
	| 'CHOREO'
	| 'RHYTHM'
	| 'PROSODY'
	| 'REUSE'
	| 'TEXTURE'
	| 'ENVELOPE'
	| 'RESIDUE'
	| 'SEGMENT'
	| 'TRANSLATION';

export type Estimator = 'rate' | 'proportion' | 'recurrence' | 'distributional' | 'structural';
export type Ramp = ReadonlyArray<readonly [number, number]>;

export interface Prior {
	/** Human-typical value in ramp units (the x where the ramp gives 20). */
	mu: number;
	/** Prior pseudo-exposure, in exposure units. */
	m: number;
	/** Prior overdispersion. */
	phi0: number;
}

export interface FeatureDef {
	id: string;
	label: string;
}

export interface ChannelDef {
	id: ChannelId;
	name: string;
	family: Family;
	axis: 'authorship' | 'translation';
	registers: Array<'prose' | 'structural' | 'quoted' | 'code'>;
	/** +1: higher value is more machine-like. −1: lower value is more machine-like. */
	direction: 1 | -1;
	/** One-way channels (absence carries little weight) vs two-way. Used by the ensemble (W3b). */
	oneWay: boolean;
	estimator: Estimator;
	features: FeatureDef[];
	/** Below this exposure (in `exposureUnit`) the channel abstains. */
	minExposure: number;
	exposureUnit: 'words' | 'sentences' | 'paragraphs' | 'units' | 'marks' | 'lines';
	lang: 'native' | 'agnostic';
	/** Display ramp: value (in ramp units) → 0–100. */
	ramp: Ramp;
	/** ramp units = (K/E) × unitScale, e.g. 1000 for "per 1k words", 10 for "per 10 sentences". */
	unitScale: number;
	unitLabel: string;
	prior: Prior;
	/**
	 * Shrink the rate estimate toward the human prior (default true; ENGINE-SPEC §3.2, C-SHRINK). `false` is for
	 * RESIDUE channels (OB-01), where one hit is near-conclusive by design: the estimate and its interval are the raw
	 * rate × unitScale (Garwood, widened by √φ), never mapped through the shrinkage. κ is still the data weight
	 * E/φ / (E/φ + m) from the prior's pseudo-exposure, so the ensemble weighs a short text's channel accordingly;
	 * the deviation is unchanged. Honoured by `fitRate` (document, segment and unit estimates alike).
	 */
	shrink?: boolean;
	/** Seed ensemble weight (pack channels: 1.5 × pack weight). */
	weight: number;
	/** Seed CH_META nd (damping of negative evidence for one-way channels). */
	nd: number;
	/** C-IDs that explain differences from the seed. */
	changes: string[];
}

export interface EvidenceSpans {
	start: Uint32Array;
	end: Uint32Array;
	feature: Uint8Array;
	weight: Float32Array;
}

export interface Interval {
	lo: number;
	hi: number;
}

export interface DocEstimate {
	/** Weighted events and exposure (rate/proportion); for other estimators the effective K and E used for κ. */
	K: number;
	E: number;
	/** K/E in exposure units (before ×unitScale), or the channel's raw statistic when not a rate. */
	raw: number;
	/** Shrunken estimate in ramp units (the value the ramp is applied to). */
	estimate: number;
	ci90: [number, number];
	/** Standardised deviation from the human prior, signed by direction (positive = machine-like). */
	deviation: number;
	phi: number;
	/** Data weight 0–1. */
	kappa: number;
	/** 0–100 evidence strength. */
	score: number;
	scoreCi90: [number, number];
}

export interface ChannelSegment {
	paraStart: number;
	paraEnd: number;
	K: number;
	E: number;
	estimate: number;
	ci90: [number, number];
	score: number;
	/** Significant against the human reference (FDR q = 0.05). */
	elevated: boolean;
	/** Significant against both the reference and the document's own background. */
	hotspot: boolean;
	pRef: number;
	pBg: number;
	rateRatioRef?: number;
	rateRatioBg?: number;
}

export interface UnitEstimate {
	kind: 'chapter' | 'scene';
	index: number;
	paraStart: number;
	paraEnd: number;
	K: number;
	E: number;
	estimate: number;
	ci90: [number, number];
	score: number;
	elevated: boolean;
	pRef: number;
}

export interface ChannelResult {
	id: ChannelId;
	status: 'scored' | 'abstain';
	reason?: string;
	/** Extra human-readable notes ("uncalibrated for es", "shortLookback"). */
	notes: string[];
	doc: DocEstimate;
	/** Event-type channels only: per-paragraph weighted events and exposure (length = doc.paragraphs.count). */
	perParagraph?: { k: Float64Array; e: Float64Array };
	segments: ChannelSegment[];
	/**
	 * Single paragraphs (≥ 2 weighted events) that are individually significant against the human
	 * reference (FDR-controlled with the segments). Needed because a one-paragraph residue event
	 * ("As an AI language model…") cannot form its own segment under the ≥ 1 expected-event rule
	 * (register: C-HOT-SPIKE, ENGINE-SPEC §3.4 / T-HOT-03).
	 */
	spikes: ChannelSegment[];
	units: UnitEstimate[];
	evidence: EvidenceSpans;
	/**
	 * Set when this channel's `elevated`/`hotspot` flags are withheld (operator decision ED-13: descending two-way
	 * channels whose priors come from the ramps until C2). Segments, units, spikes and their p-values are still
	 * computed; only the flags are cleared and the channel is excluded from the FDR family.
	 */
	flagsWithheld?: string;
	/** Named secondary features (e.g. PROC-01 R6(L_ref)); values in their own units. */
	features: Record<string, number>;
	/** Seed-format evidence summary lines (INV §1), top items first. */
	summary: string[];
}

/** What a rate/proportion channel reports from its single scan. */
export interface Measurement {
	/** Set to abstain (register absent, observable 'none', unsupported language, ...). */
	abstain?: string;
	/** Weighted events per paragraph (length = paragraphs.count). */
	k: Float64Array;
	/** Exposure per paragraph, in the channel's exposureUnit. */
	e: Float64Array;
	evidence?: EvidenceBuilder;
	notes?: string[];
	features?: Record<string, number>;
	summary?: string[];
	/** Optional override of the display estimate (ramp units) for channels whose display is a composite. */
	displayOverride?: number;
	/**
	 * Side data for the bench's measurement layer (BENCH-V2-SPEC §5): scalars and per-paragraph arrays that are not part of
	 * the channel's result. Kept off `ChannelResult` (see `auxOf`), so no export, golden or oracle output changes.
	 */
	aux?: Record<string, number | Float64Array>;
}

export interface EvidenceBuilder {
	add(start: number, end: number, feature: number, weight: number): void;
	build(): EvidenceSpans;
	readonly length: number;
}

export interface RunContext {
	doc: Doc;
	/** Deterministic RNG seeded from the document hash (xoshiro128**). */
	rng: () => number;
	/** Joint segmentation, present for distributional/structural channels (computed after the event channels). */
	joint?: JointSegmentation;
	/** Results of channels already run, by id (event channels first). */
	results: Partial<Record<ChannelId, ChannelResult>>;
	/**
	 * Bench measurement only (BENCH-V2-SPEC §5.1): document-level quantities fixed at baseline. When present, the
	 * context-dependent channels read them instead of recomputing them from the (partial) document in `doc`.
	 * `analyseCore` never sets it, so the detector is unchanged.
	 */
	frozen?: import('../measure/frozen.ts').FrozenContext;
}

export interface ChannelImpl {
	def: ChannelDef;
	/** Runs the channel. Never throws for bad text: abstains with a reason. */
	run(ctx: RunContext): ChannelResult;
	/** Which pass: 'event' channels first (they feed joint segmentation), then 'derived'. */
	phase: 'event' | 'derived';
	/**
	 * The per-paragraph event scan of a rate/proportion channel, without the document-level fit or the abstention
	 * (the bench's `measureRange` calls it on a context Doc, with `ctx.frozen`).
	 */
	measure?(ctx: RunContext): Measurement;
}
